import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  App,
  Badge,
  Button,
  Card,
  Descriptions,
  Empty,
  Skeleton,
  Space,
  Spin,
  Tabs,
  Tag,
} from 'antd';
import {
  ArrowLeftOutlined,
  CopyOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { applicationApi } from '@/api/application.api';
import { agentApi } from '@/api/agent.api';
import { langfuseApi } from '@/api/langfuse.api';
import { TraceDetailsDialog } from '@/components/TraceDetailsDialog';
import {
  Card as UICard,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge as UIBadge } from '@/components/ui/badge';
import { Button as UIButton } from '@/components/ui/button';
import { Skeleton as UISkeleton } from '@/components/ui/skeleton';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { DescriptionText } from '@/components/ui/description-text';
import { create } from '@bufbuild/protobuf';
import { Trace } from '@/proto/agentplatform/v1/langfuse_pb';
import { ListTracesRequestSchema } from '@/proto/agentplatform/httpv2/observability_pb';
import { AccountStatus, ChannelAccount, ChannelType } from '@/proto/agentplatform/v1/channel_pb';
import { InstanceStatus, ConfigSyncStatus } from '@/proto/agentplatform/v1/instance_pb';
import { formatTimestamp } from '@/utils/trace.utils';
import FaultToleranceView from '@/components/Agents/FaultToleranceView';
import {
  FT_SLOT_KEYS,
  hasFaultToleranceSlotParams,
  parseFaultToleranceConfig,
  parseFaultToleranceSlotParams,
} from '@/components/Agents/faultTolerance';
import { BackgroundTaskTab } from './BackgroundTaskTab';
import './styles/AgentAppDetail.css';

/** 实例 params 中的容错运行时槽位 key（在「容错配置」tab 单独展示） */
const FT_PARAM_KEYS = new Set<string>(FT_SLOT_KEYS);

const LIFECYCLE_LABEL: Record<string, string> = {
  PENDING: '待启动',
  RUNNING: '运行中',
};
const LIFECYCLE_COLOR: Record<string, 'default' | 'success' | 'warning'> = {
  PENDING: 'default',
  RUNNING: 'success',
};

function formatTimestamp2(timestamp: number | bigint | undefined) {
  if (!timestamp) return '-';
  const date = new Date(Number(timestamp) / 1000000);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function CopyTextButton({ text }: { text: string }) {
  const { message } = App.useApp();
  return (
    <Button
      type="text"
      size="small"
      icon={<CopyOutlined />}
      onClick={() => {
        navigator.clipboard.writeText(text);
        message.success('已复制');
      }}
    />
  );
}

function BasicInfoTab({ app }: { app: any }) {
  const [revealedTokens, setRevealedTokens] = useState<Set<string>>(new Set());
  // 容错相关参数在「容错配置」tab 单独展示，这里不重复列出
  const visibleParams = Object.entries(app.params ?? {}).filter(
    ([key]) => !FT_PARAM_KEYS.has(key)
  ) as Array<[string, any]>;

  const toggleToken = (key: string) => {
    setRevealedTokens((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card size="small">
        <Descriptions column={2} size="middle">
          <Descriptions.Item label="实例 ID">
            <Space size={4}>
              <code style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>
                {app.id}
              </code>
              <CopyTextButton text={app.id} />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="名称">{app.name}</Descriptions.Item>
          <Descriptions.Item label="关联模版 ID">
            <Space size={4}>
              <code style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>
                {app.agentId}
              </code>
              <CopyTextButton text={app.agentId} />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="描述">
            <DescriptionText text={app.description} maxChars={30} />
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatTimestamp2(app.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{formatTimestamp2(app.updatedAt)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {visibleParams.length > 0 && (
        <Card size="small" title="参数配置">
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <colgroup>
              <col style={{ width: '25%' }} />
              <col style={{ width: '75%' }} />
            </colgroup>
            <thead>
              <tr style={{ background: 'var(--genui-color-fill-tertiary, #f5f5f5)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>参数名</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>值</th>
              </tr>
            </thead>
            <tbody>
              {visibleParams.map(([key, value]) => {
                const isToken = key.toUpperCase().includes('TOKEN');
                const isRevealed = revealedTokens.has(key);
                const displayValue = isToken && !isRevealed ? '****************' : value;
                return (
                  <tr
                    key={key}
                    style={{ borderTop: '1px solid var(--genui-color-border-secondary, #ebeaed)' }}
                  >
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                      {key}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>
                      <Space size={4}>
                        <span
                          style={{
                            display: 'inline-block',
                            maxWidth: 320,
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle',
                          }}
                        >
                          {displayValue || '-'}
                        </span>
                        {isToken && (
                          <Button
                            type="text"
                            size="small"
                            icon={isRevealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                            onClick={() => toggleToken(key)}
                            title={isRevealed ? '隐藏' : '显示'}
                          />
                        )}
                      </Space>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </Space>
  );
}

function TraceTab({ appId }: { appId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const { data: appData, isLoading: isAppLoading } = useQuery({
    queryKey: ['application', appId],
    queryFn: () => applicationApi.get(appId),
    enabled: !!appId,
  });

  const agentId = appData?.agentId;
  const instanceId = appData?.id;

  const {
    data,
    isLoading: isTraceLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['app-traces', appId, agentId, instanceId, page, pageSize],
    queryFn: async () => {
      const tags: string[] = [];
      if (agentId) tags.push(`agent=${agentId}`);
      if (instanceId) tags.push(`instance=${instanceId}`);
      const request = create(ListTracesRequestSchema, { tags, page, pageSize });
      return langfuseApi.listTraces(request);
    },
    enabled: !!appData,
  });

  const traces = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const handleTraceClick = (traceId: string) => {
    setSelectedTraceId(traceId);
    setDetailsDialogOpen(true);
  };

  if (isAppLoading || isTraceLoading) {
    return (
      <div className="space-y-6">
        <UISkeleton className="h-8 w-64" />
        <UISkeleton className="h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <UICard>
        <CardContent className="py-12">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">加载失败</h3>
            <p className="text-muted-foreground mb-4">无法加载 Trace 数据</p>
          </div>
        </CardContent>
      </UICard>
    );
  }

  return (
    <>
      <UICard>
        <CardHeader>
          <CardTitle>调用记录</CardTitle>
          <CardDescription>共 {totalCount} 条记录</CardDescription>
        </CardHeader>
        <CardContent>
          {traces.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <svg
                className="w-16 h-16 mx-auto mb-4 opacity-50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2"
                />
              </svg>
              <p className="text-lg font-medium mb-2">暂无调用记录</p>
              <p className="text-sm">调用记录将在实例被调用时显示</p>
            </div>
          ) : (
            <>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        Trace ID
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        时间
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        环境
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        标签
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {traces.map((trace: Trace) => (
                      <tr
                        key={trace.id}
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => handleTraceClick(trace.id)}
                      >
                        <td className="px-3 py-3 font-mono text-xs max-w-[150px] truncate">
                          {trace.id}
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">
                          {formatTimestamp(trace.timestamp)}
                        </td>
                        <td className="px-3 py-3">
                          {trace.environment ? (
                            <UIBadge variant="outline" className="text-xs">
                              {trace.environment}
                            </UIBadge>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1">
                            {trace.tags.slice(0, 2).map((tag: string) => (
                              <UIBadge key={tag} variant="outline" className="text-xs">
                                {tag}
                              </UIBadge>
                            ))}
                            {trace.tags.length > 2 && (
                              <UIBadge variant="outline" className="text-xs">
                                +{trace.tags.length - 2}
                              </UIBadge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    第 {page} / {totalPages || 1} 页，共 {totalCount} 条
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">每页</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-8 rounded-md border border-input bg-transparent px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-muted-foreground">条</span>
                  </div>
                </div>
                {totalPages > 1 && (
                  <div className="flex gap-2">
                    <UIButton
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || isFetching}
                    >
                      <ChevronLeftIcon className="h-4 w-4 mr-1" />
                      上一页
                    </UIButton>
                    <UIButton
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages || isFetching}
                    >
                      下一页
                      <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </UIButton>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </UICard>
      <TraceDetailsDialog
        traceId={selectedTraceId}
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
      />
    </>
  );
}

function ChannelTab({ appId }: { appId: string }) {
  const getChannelTypeLabel = (channelType: ChannelType): string => {
    switch (channelType) {
      case ChannelType.FEISHU:
        return 'FEISHU';
      case ChannelType.QQ:
        return 'QQ';
      case ChannelType.CHANNEL_TYPE_UNSPECIFIED:
      default:
        return 'CHANNEL_TYPE_UNSPECIFIED';
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['app-channel-accounts', appId],
    queryFn: () => applicationApi.listChannelAccounts(appId),
    enabled: !!appId,
  });

  const channelAccounts = data?.data ?? [];

  if (isLoading) {
    return (
      <Card size="small" title="渠道">
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 64 }}
        >
          <Spin tip="加载中..." />
        </div>
      </Card>
    );
  }

  return (
    <Card size="small" title="渠道">
      {channelAccounts.length === 0 ? (
        <Empty description="暂无渠道账号 · 当前实例尚未关联渠道账号" />
      ) : (
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--genui-color-fill-tertiary, #f5f5f5)' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>
                渠道 ID / 名称
              </th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>类型</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>状态</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>凭证</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>更新时间</th>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500 }}>
                最后离线时间
              </th>
            </tr>
          </thead>
          <tbody>
            {channelAccounts.map((account: ChannelAccount) => (
              <tr
                key={account.id}
                style={{ borderTop: '1px solid var(--genui-color-border-secondary, #ebeaed)' }}
              >
                <td style={{ padding: '10px 12px' }}>
                  <Space direction="vertical" size={2}>
                    <code style={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {account.id || '-'}
                    </code>
                    <span>{account.name || '-'}</span>
                  </Space>
                </td>
                <td style={{ padding: '10px 12px' }}>{getChannelTypeLabel(account.type)}</td>
                <td style={{ padding: '10px 12px' }}>
                  {account.status === AccountStatus.ONLINE ? (
                    <Tag color="success">在线</Tag>
                  ) : (
                    <Tag color="error">离线</Tag>
                  )}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {account.credential ? (
                    <Space direction="vertical" size={2} style={{ fontSize: 12 }}>
                      <Space size={4}>
                        <span style={{ color: 'var(--genui-color-text-secondary)', minWidth: 60 }}>
                          AppID
                        </span>
                        <code style={{ fontFamily: 'monospace' }}>
                          {account.credential.appId || '-'}
                        </code>
                      </Space>
                      <Space size={4}>
                        <span style={{ color: 'var(--genui-color-text-secondary)', minWidth: 60 }}>
                          AppSecret
                        </span>
                        <code style={{ fontFamily: 'monospace' }}>
                          {account.credential.appSecret || '-'}
                        </code>
                      </Space>
                    </Space>
                  ) : (
                    <span style={{ color: 'var(--genui-color-text-secondary)' }}>-</span>
                  )}
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--genui-color-text-secondary)' }}>
                  {formatTimestamp2(account.updatedAt)}
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--genui-color-text-secondary)' }}>
                  {account.lastOfflineAt > 0n ? formatTimestamp2(account.lastOfflineAt) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/** 容错配置：以模板配置为底，实例 params 存在槽位 key 时按槽位覆盖（与模板详情页 UI 一致） */
function FaultToleranceTab({ app }: { app: any }) {
  const params: Record<string, string> | undefined = app.params;
  const hasOverride = hasFaultToleranceSlotParams(params);

  const { data: agent, isLoading } = useQuery({
    queryKey: ['agent', app.agentId],
    queryFn: () => agentApi.getAgent(app.agentId),
    enabled: !!app.agentId,
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 64 }}>
        <Spin tip="加载中..." />
      </div>
    );
  }

  const template = parseFaultToleranceConfig(agent?.advancedConfig?.faultTolerance);
  const value = hasOverride ? parseFaultToleranceSlotParams(params, template) : template;

  return (
    <FaultToleranceView
      value={value}
      header={
        <Tag color={hasOverride ? 'blue' : 'default'}>
          {hasOverride ? '实例已覆盖模板配置' : '继承自模板配置'}
        </Tag>
      }
    />
  );
}

export default function AgentAppDetail() {
  const { id: agentId, appId } = useParams<{ id: string; appId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('basic');

  const returnTo = (location.state as { returnTo?: string })?.returnTo;
  const {
    data: app,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['application', appId],
    queryFn: () => applicationApi.get(appId!),
    enabled: !!appId,
  });

  const backTarget = useMemo(
    () => returnTo || (agentId ? `/agents/${agentId}` : '/applications'),
    [returnTo, agentId]
  );

  if (isLoading) {
    return (
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <Skeleton.Input active style={{ width: 256, height: 48 }} />
        <Skeleton active paragraph={{ rows: 4 }} />
        <Skeleton active paragraph={{ rows: 4 }} />
      </Space>
    );
  }

  if (error || !app) {
    return (
      <Card size="small">
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Badge status="error" />
          <h3 style={{ margin: '12px 0 8px', fontSize: 18 }}>加载失败</h3>
          <p style={{ color: 'var(--genui-color-text-secondary)', marginBottom: 16 }}>
            无法加载应用详情
          </p>
          <Button onClick={() => navigate(backTarget)}>返回列表</Button>
        </div>
      </Card>
    );
  }

  const tabs = [
    { key: 'basic', label: '基本信息', children: <BasicInfoTab app={app} /> },
    { key: 'trace', label: 'Trace', children: <TraceTab appId={app.id} /> },
    { key: 'channel', label: '渠道', children: <ChannelTab appId={app.id} /> },
    { key: 'tasks', label: '后台任务', children: <BackgroundTaskTab appId={app.id} /> },
    { key: 'faultTolerance', label: '容错配置', children: <FaultToleranceTab app={app} /> },
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Space size={8} align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(backTarget)} />
          <div>
            <Space size={8} align="center">
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>
                {app.name}
              </h1>
              {app.status === InstanceStatus.RUNNING ? (
                <>
                  <Tag color={LIFECYCLE_COLOR.RUNNING}>
                    <span className="id-status-dot running" />
                    {LIFECYCLE_LABEL.RUNNING}
                  </Tag>
                  {app.configSyncStatus === ConfigSyncStatus.OUTDATED && (
                    <Tag color="warning">
                      <span className="id-status-dot outdated" />
                      需更新
                    </Tag>
                  )}
                </>
              ) : app.status === InstanceStatus.PENDING ? (
                <Tag color={LIFECYCLE_COLOR.PENDING}>
                  <span className="id-status-dot pending" />
                  {LIFECYCLE_LABEL.PENDING}
                </Tag>
              ) : null}
            </Space>
            <div style={{ marginTop: 2 }}>
              <DescriptionText text={app.description} maxChars={40} className="text-[13px]" />
            </div>
          </div>
        </Space>
        <Button
          icon={<EditOutlined />}
          onClick={() =>
            navigate(`/applications/${appId}/edit`, {
              state: { returnTo: `/agents/${agentId}/apps/${appId}` },
            })
          }
        >
          编辑
        </Button>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />
    </Space>
  );
}
